// src/components/StockTransfer.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import {
  collection,
  onSnapshot,
  doc,
  runTransaction,
} from 'firebase/firestore';

const StockTransfer = () => {
  const [items, setItems] = useState([]);
  const [boxInputs, setBoxInputs] = useState({});

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'stock_store'), (snapshot) => {
      const itemList = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setItems(itemList);

      setBoxInputs((prev) => {
        const next = { ...prev };
        for (const it of itemList) {
          if (!(it.id in next)) next[it.id] = '';
        }
        Object.keys(next).forEach((k) => {
          if (!itemList.find((it) => it.id === k)) delete next[k];
        });
        return next;
      });
    });
    return () => unsub();
  }, []);

  const fmt2 = (v) => Number(v ?? 0).toFixed(2);

  const calcBoxes = (item) => {
    const kg = Number(item?.totalKg ?? 0);
    const bw = Number(item?.boxWeight ?? 0);
    return bw > 0 ? kg / bw : 0;
  };

  const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

  const handleInputChange = (itemId, value) => {
    setBoxInputs((prev) => ({ ...prev, [itemId]: value }));
  };

  const handleTransfer = async () => {
    const entries = Object.entries(boxInputs).filter(
      ([, val]) => val && !isNaN(val) && parseInt(val, 10) > 0
    );
    if (entries.length === 0) return alert('⚠️ Enter valid box amounts');

    for (const [itemId, countStr] of entries) {
      const item = items.find((i) => i.id === itemId);
      const boxCount = parseInt(countStr, 10);
      const bwFallback = Number(item?.boxWeight || 0);

      const storeRef = doc(db, 'stock_store', itemId);
      const shopRef = doc(db, 'stock_shop', itemId);

      try {
        await runTransaction(db, async (tx) => {
          const storeSnap = await tx.get(storeRef);
          const shopSnap = await tx.get(shopRef);

          if (!storeSnap.exists()) throw new Error(`Store item "${itemId}" missing`);
          const sData = storeSnap.data();

          const effBw = Number(sData.boxWeight || bwFallback || 0);
          if (effBw <= 0) throw new Error(`Invalid boxWeight for "${itemId}"`);

          const kgToMove = effBw * boxCount;

          const currStoreKg = Number(sData.totalKg || 0);
          if (kgToMove > currStoreKg) throw new Error(`Not enough stock for "${itemId}"`);

          const newStoreKg = round2(currStoreKg - kgToMove);
          const newStoreBoxes = round2(newStoreKg / effBw);

          if (shopSnap.exists()) {
            const shData = shopSnap.data();
            const shopBw = Number(shData.boxWeight || effBw);
            const currShopKg = Number(shData.totalKg || 0);
            const newShopKg = round2(currShopKg + kgToMove);
            const newShopBoxes = shopBw > 0 ? round2(newShopKg / shopBw) : 0;

            tx.update(storeRef, { totalKg: newStoreKg, boxes: newStoreBoxes });
            tx.update(shopRef, { totalKg: newShopKg, boxes: newShopBoxes });
          } else {
            const base = {
              id: itemId,
              description: sData.description ?? item.description ?? '',
              boxWeight: effBw,
              pricePerKg: sData.pricePerKg ?? item.pricePerKg ?? null,
              unitsPerBox: sData.unitsPerBox ?? item.unitsPerBox ?? null,
              weightPerUnit: sData.weightPerUnit ?? item.weightPerUnit ?? null,
            };
            const newShopKg = round2(kgToMove);
            const newShopBoxes = round2(newShopKg / effBw);

            tx.update(storeRef, { totalKg: newStoreKg, boxes: newStoreBoxes });
            tx.set(shopRef, { ...base, totalKg: newShopKg, boxes: newShopBoxes });
          }
        });

        setBoxInputs((prev) => ({ ...prev, [itemId]: '' }));
      } catch (e) {
        alert(`❌ ${e.message}`);
      }
    }

    alert('✅ Transfer completed!');
  };

  const handleAddToStore = async () => {
    const entries = Object.entries(boxInputs).filter(
      ([, val]) => val && !isNaN(val) && parseInt(val, 10) > 0
    );
    if (entries.length === 0) return alert('⚠️ Enter valid box amounts');

    for (const [itemId, countStr] of entries) {
      const item = items.find((i) => i.id === itemId);
      const boxCount = parseInt(countStr, 10);
      const bw = Number(item?.boxWeight || 0);
      const kgToAdd = bw * boxCount;

      const storeRef = doc(db, 'stock_store', itemId);

      try {
        await runTransaction(db, async (tx) => {
          const snap = await tx.get(storeRef);
          if (snap.exists()) {
            const data = snap.data();
            const effBw = Number(data.boxWeight || bw || 0);
            if (effBw <= 0) throw new Error(`Invalid boxWeight for "${itemId}"`);

            const currKg = Number(data.totalKg || 0);
            const newKg = round2(currKg + kgToAdd);
            const newBoxes = round2(newKg / effBw);

            tx.update(storeRef, { totalKg: newKg, boxes: newBoxes });
          } else {
            const base = {
              id: itemId,
              description: item.description ?? '',
              boxWeight: bw,
              pricePerKg: item.pricePerKg ?? null,
              unitsPerBox: item.unitsPerBox ?? null,
              weightPerUnit: item.weightPerUnit ?? null,
            };
            const newKg = round2(kgToAdd);
            const newBoxes = bw > 0 ? round2(newKg / bw) : 0;

            tx.set(storeRef, { ...base, totalKg: newKg, boxes: newBoxes });
          }
        });

        setBoxInputs((prev) => ({ ...prev, [itemId]: '' }));
      } catch (e) {
        alert(`❌ ${e.message}`);
      }
    }

    alert('✅ Added to store!');
  };

  return (
    <div className="h2Align">
      <h2>📦 Stock Transfer / Warehouse Intake</h2>

      <div className="table-responsive">
        <table
          className="table table--stack"
          border="1"
          cellPadding="8"
          style={{ width: '100%', textAlign: 'center' }}
        >
          <thead>
            <tr>
              <th>Item Code</th>
              <th>Description</th>
              <th>Box Weight (kg)</th>
              <th>Boxes in Warehouse</th>
              <th>Total in Warehouse (kg)</th>
              <th>📥 Boxes to Add/Transfer</th>
              <th>📦 Total Weight (kg)</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const count = parseInt(boxInputs[item.id], 10) || 0;
              const totalKgForInput = count * Number(item.boxWeight || 0);

              return (
                <tr key={item.id}>
                  <td data-label="Item Code">{item.id}</td>
                  <td data-label="Description">{item.description}</td>
                  <td data-label="Box Weight (kg)">{fmt2(item.boxWeight)}</td>
                  <td data-label="Boxes in Warehouse">{fmt2(calcBoxes(item))}</td>
                  <td data-label="Total in Warehouse (kg)">{fmt2(item.totalKg)}</td>
                  <td data-label="📥 Boxes to Add/Transfer">
                    <input
                      type="number"
                      min="0"
                      value={boxInputs[item.id] || ''}
                      onChange={(e) => handleInputChange(item.id, e.target.value)}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="input"
                      style={{ width: '100%' }}
                    />
                  </td>
                  <td data-label="📦 Total Weight (kg)">{fmt2(totalKgForInput)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ textAlign: 'center', marginTop: 20 }}>
        <button
          onClick={handleAddToStore}
          className="btn btn-primary"
          style={{ marginRight: 10 }}
        >
          ➕ Add to Store
        </button>
        <button onClick={handleTransfer} className="btn btn-primary">
          🔁 Transfer to Shop
        </button>
      </div>
    </div>
  );
};

export default StockTransfer;